import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { createEvent } from "core/utils/tracker";
import { useQuery, useSubscription } from "@apollo/client";
import ReactGA from "react-ga4";
import { ContentQuery } from "player/queries";
import LoadingScreen from "core/components/LoadingScreen";
import ErrorScreen from "core/components/ErrorScreen";
import EmbedHelmet from "player/components/EmbedHelmet";
import NoMatchScreen from "player/components/NoMatchScreen";
import Player from "core/components/Player";
import RetargetPixels from "core/components/RetargetPixels";
import { ContentStreamStatusChangedSubscription } from "core/subscriptions";

const StyledEmbed = styled("div")({
  overflow: "hidden",
  width: "100%",
  height: "100%",
  position: "relative",
});

const STREAM = "STREAM";
const EMBED = "EMBED";

/**
 * Коллбек который срабатывает в случае успешной загрузки контента.
 *
 * @param {Object} data
 * @param {Object} data.player - Контент
 * @param {String} [data.player.access.token] - Токен контента
 * @param {Object} [data.player.widget] - Виджет контента
 * @param {String} [data.player.widget.id] - ID Виджета
 */
const onCompleted = (data) => {
  if (data.content) {
    createEvent("page", "load");

    if (typeof window !== "undefined") {
      ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname,
        title: data.content.title,
      });
    }
  }
};

const sponsorPlayerLogoPlaces = {
  VIDEO: "VIDEO_IN_EMBED",
  STREAM: "STREAM_IN_EMBED",
  // SHORTY: undefined - для шортисов не нужны логотипы плеера
};

const sponsorPlayerButtonPlaces = {
  VIDEO: "VIDEO_IN_EMBED",
  STREAM: "STREAM_IN_EMBED",
  // SHORTY: undefined - для шортисов не нужны кнопки плеера
};

const sponsorVideoPlaces = {
  VIDEO: "VIDEO_IN_EMBED",
  STREAM: "STREAM_IN_EMBED",
  SHORTY: "SHORTY_IN_EMBED", // Будет формировать PREROLL_IN_SHORTY_IN_EMBED и POSTROLL_IN_SHORTY_IN_EMBED
};

const sponsorChatMessagePlaces = {
  STREAM: "STREAM_IN_EMBED",
  // SHORTY не поддерживает чат
};

const sponsorInStreamBannerPlaces = {
  VIDEO: "VIDEO_IN_EMBED",
  STREAM: "STREAM_IN_EMBED",
  // SHORTY: undefined - для шортисов не нужны in-stream баннеры
};

const sponsorCornerBannerPlaces = {
  VIDEO: "VIDEO_IN_EMBED",
  STREAM: "STREAM_IN_EMBED",
  // SHORTY: undefined - для шортисов не нужны угловые баннеры
};

/**
 * Основной компонент плеера, который доступен по адресу /embed/:contentId
 */
const Embed = ({
  contentId,
  autoPlay,
  loop,
  muted,
  start,
  finish,
  targetStreamId,
  controls,
  coverFitMode,
}) => {
  const [played, setPlayed] = useState(false);

  const onPlay = () => {
    setPlayed(true);
  };

  const { data, loading, error } = useQuery(ContentQuery, {
    variables: {
      contentId,
      source: "EMBED",
    },
    onCompleted,
  });

  useSubscription(ContentStreamStatusChangedSubscription, {
    skip: !process.browser || data?.content?.type !== STREAM,
    onData: ({ client, data }) => {
      if (data?.data?.contentStreamStatusChanged?.rejectModeration) {
        client.resetStore();
      }
    },
    variables: {
      id: contentId,
    },
  });

  const content = data?.content;

  if (loading && !data) return <LoadingScreen />;

  if (error) return <ErrorScreen error={error} />;

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  const sponsorPlayerLogoPlace = sponsorPlayerLogoPlaces[content.type];
  const sponsorPlayerButtonPlace = sponsorPlayerButtonPlaces[content.type];
  const sponsorVideoPlace = sponsorVideoPlaces[content.type];
  const sponsorChatMessagePlace = sponsorChatMessagePlaces[content.type];
  const sponsorInStreamBannerPlace = sponsorInStreamBannerPlaces[content.type];
  const sponsorCornerBannerPlace = sponsorCornerBannerPlaces[content.type];

  const { disableAutoPauseInEmbed } = content.rightholder;

  const autoPause = !disableAutoPauseInEmbed;

  return (
    <StyledEmbed id="embed">
      <EmbedHelmet content={content} />

      <Player
        key={contentId}
        onPlay={onPlay}
        content={content}
        autoPlay={autoPlay}
        autoPause={autoPause}
        controls={controls}
        loop={loop}
        muted={muted}
        triggerPostMessage
        fullscreenTarget="embed"
        start={start}
        finish={finish}
        disableTitleLink={content.disableEmbedTitleLink}
        sponsorPlayerLogoPlace={sponsorPlayerLogoPlace}
        sponsorPlayerButtonPlace={sponsorPlayerButtonPlace}
        sponsorVideoPlace={sponsorVideoPlace}
        sponsorChatMessagePlace={sponsorChatMessagePlace}
        sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
        sponsorCornerBannerPlace={sponsorCornerBannerPlace}
        targetStreamId={targetStreamId}
        coverFitMode={coverFitMode}
      />

      <RetargetPixels
        rightholder={content.rightholder.id}
        viewSource={EMBED}
        sport={content.sport?.id}
        content={content.id}
        contentType={content.type}
        eventType={played ? "playVideo" : "showPlayer"}
        source="embed"
      />
    </StyledEmbed>
  );
};

Embed.propTypes = {
  contentId: PropTypes.string.isRequired,
  targetStreamId: PropTypes.string,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  start: PropTypes.number,
  finish: PropTypes.number,
  controls: PropTypes.bool,
  coverFitMode: PropTypes.bool,
};

export default Embed;
